interface LicensedUser {
  id?: string | null;
  email?: string | null;
  role?: string | null;
  status?: string | null;
  tenantId?: string | null;
}

interface LicenseLimits {
  standard?: number | null;
  viewOnly?: number | null;
}

export type ExceededLicenseType = 'STANDARD' | 'VIEW_ONLY';

const EXCLUDED_LICENSE_DOMAINS = [
  'medispend.com',
  'alucio.io',
];

export const consumesUserLicense = (email?: string | null): boolean => {
  const normalizedEmail = email?.toLowerCase() || '';

  return !EXCLUDED_LICENSE_DOMAINS.some((domain) => normalizedEmail.includes(domain));
};

export const getActiveLicensedUsers = <T extends LicensedUser>(users: T[]): T[] => (
  users.filter((user) => user.status === 'ACTIVE' && consumesUserLicense(user.email))
);

export const wouldExceedLicenseLimit = <T extends LicensedUser>(
  users: T[],
  candidateEmail: string | null | undefined,
  limit: number,
  candidateId?: string | null,
): boolean => {
  const candidateLicenseCount = consumesUserLicense(candidateEmail) ? 1 : 0;
  const existingUsers = candidateId
    ? users.filter((user) => user.id !== candidateId)
    : users;

  return getActiveLicensedUsers(existingUsers).length + candidateLicenseCount > limit;
};

const isViewOnlyUser = (user: LicensedUser): boolean => user.role === 'TenantViewOnly';

export const hasLicenseUsageChanged = (
  previousUser: LicensedUser,
  candidateUser: LicensedUser,
): boolean => {
  const previousUserConsumesLicense = previousUser.status === 'ACTIVE' &&
    consumesUserLicense(previousUser.email);
  const candidateUserConsumesLicense = candidateUser.status === 'ACTIVE' &&
    consumesUserLicense(candidateUser.email);

  return previousUserConsumesLicense !== candidateUserConsumesLicense ||
    (candidateUserConsumesLicense && (
      isViewOnlyUser(previousUser) !== isViewOnlyUser(candidateUser) ||
      previousUser.tenantId !== candidateUser.tenantId
    ));
};

export const getExceededLicenseType = <T extends LicensedUser>(
  users: T[],
  candidateUser: LicensedUser,
  limits: LicenseLimits,
  previousUser?: LicensedUser,
): ExceededLicenseType | undefined => {
  if (previousUser && !hasLicenseUsageChanged(previousUser, candidateUser)) {
    return undefined;
  }

  if (candidateUser.status !== 'ACTIVE' || !consumesUserLicense(candidateUser.email)) {
    return undefined;
  }

  const isTargetViewOnly = isViewOnlyUser(candidateUser);
  const usersInTargetPool = users.filter((user) => isViewOnlyUser(user) === isTargetViewOnly);
  const limit = isTargetViewOnly ? limits.viewOnly || 0 : limits.standard || 0;

  if (!wouldExceedLicenseLimit(
    usersInTargetPool,
    candidateUser.email,
    limit,
    candidateUser.id,
  )) {
    return undefined;
  }

  return isTargetViewOnly ? 'VIEW_ONLY' : 'STANDARD';
};
